import React, { useState, useEffect } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
  Alert,
  CircularProgress,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search,
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Close,
  Refresh,
  SupervisorAccount,
  Email,
  Lock,
  Person,
  Security,
  CheckCircle,
  Cancel,
  ContentCopy,
  Send,
  MarkEmailRead,
  HourglassEmpty,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  ADMIN_ROLES,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_DESCRIPTIONS,
  ASSIGNABLE_ROLES,
  PERMISSIONS,
  getRolePermissions,
  normalizeRole,
  userCan,
} from "../../../config/adminRoles";
import {
  CreateAdmin,
  VerifyAdminOTP,
  ResendAdminOTP,
  GetAllAdmins,
  UpdateAdmin,
  DeleteAdmin,
  resetAdminOTPState,
  clearError,
  clearSuccess,
} from "../../../redux/reducer/AdminOTP";

// The narrower permissions that exist specifically to be handed out one
// admin at a time, on top of whatever role they hold — see
// ots-server/config/permissions.js for why each one is scoped the way it is.
const EXTRA_PERMISSION_OPTIONS = [
  {
    value: PERMISSIONS.VIEW_OTS_FEE_RECEIPTS,
    label: "View OTS Fee Receipts",
    description: "See the cash-booking fee queue, without the power to verify or reject one.",
  },
  {
    value: PERMISSIONS.VIEW_OWNER_FOLLOWUPS,
    label: "Owner Follow-ups",
    description:
      "Requests an owner hasn't reviewed, payments they haven't confirmed, overdue move-ins.",
  },
  {
    value: PERMISSIONS.VIEW_TENANT_FOLLOWUPS,
    label: "Tenant Follow-ups",
    description:
      "Tenants stuck at sign-up, unpaid bookings, cash/online payment status, move-out requests.",
  },
  {
    value: PERMISSIONS.MANAGE_PAYOUTS,
    label: "Prepare Owner Payouts",
    description:
      "See owners' payout claims and get them ready. Only the super admin can release money.",
  },
];

const AdminManagement = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { validateUser } = useSelector((state) => state);
  const { loading, admins, pendingAdmin, otpSent, error, successMessage } = useSelector(
    (state) => state.adminOTP
  );
  const currentUser = validateUser?.UserValidate?.data?.user;
  // No role means no access — there is no default role to fall back on
  const currentUserRole = normalizeRole(currentUser?.adminRole);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterRole, setFilterRole] = useState("all");

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    adminRole: ADMIN_ROLES.REVIEWER,
    extraPermissions: [],
  });
  const [showPassword, setShowPassword] = useState(false);

  // OTP states
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [otpEmail, setOtpEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // Fetch admins on mount
  useEffect(() => {
    dispatch(GetAllAdmins());
  }, [dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccess());
    }
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [successMessage, error, dispatch]);

  // Handle OTP sent - open verification dialog
  useEffect(() => {
    if (otpSent && pendingAdmin) {
      setOtpEmail(pendingAdmin.email);
      setCreateDialogOpen(false);
      setOtpDialogOpen(true);
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpSent, pendingAdmin]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const isSuperAdmin = currentUserRole === ADMIN_ROLES.SUPER_ADMIN;

  // Filter admins
  const filteredAdmins = (admins || []).filter((admin) => {
    const matchesSearch =
      (admin.name?.toLowerCase() || "").includes(searchQuery.toLowerCase()) ||
      (admin.email?.toLowerCase() || "").includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === "all" || admin.adminRole === filterRole;
    return matchesSearch && matchesRole;
  });

  const handleCreateAdmin = () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error("Please fill all required fields");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    dispatch(CreateAdmin(formData))
      .unwrap()
      .catch((error) => {
        toast.error(error?.message || "Failed to create admin");
      });
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOtp = () => {
    const otp = otpValues.join("");
    if (otp.length !== 6) {
      toast.error("Please enter complete 6-digit OTP");
      return;
    }

    dispatch(VerifyAdminOTP({ email: otpEmail, otp })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        setOtpDialogOpen(false);
        setOtpValues(["", "", "", "", "", ""]);
        dispatch(resetAdminOTPState());
        dispatch(GetAllAdmins()); // Refresh admin list
      }
    });
  };

  const handleResendOtp = () => {
    if (pendingAdmin?._id) {
      dispatch(ResendAdminOTP({ adminId: pendingAdmin._id })).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          setResendCooldown(60); // 60 second cooldown
        }
      });
    }
  };

  const handleUpdateAdmin = () => {
    if (!selectedAdmin) return;

    dispatch(
      UpdateAdmin({
        adminId: selectedAdmin._id,
        data: {
          name: formData.name,
          email: formData.email,
          adminRole: formData.adminRole,
          ...(formData.password && { password: formData.password }),
          // Super admin already holds everything regardless — the server
          // ignores this for that account anyway, but there is nothing
          // meaningful to send.
          ...(selectedAdmin?.adminRole !== ADMIN_ROLES.SUPER_ADMIN && {
            adminPermissions: formData.extraPermissions,
          }),
        },
      })
    ).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        setEditDialogOpen(false);
        resetForm();
      }
    });
  };

  const handleDeleteAdmin = async (admin) => {
    if (admin.adminRole === ADMIN_ROLES.SUPER_ADMIN) {
      toast.error("Cannot delete Super Admin!");
      return;
    }

    const result = await Swal.fire({
      title: "Delete Admin?",
      text: `Are you sure you want to delete ${admin.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      dispatch(DeleteAdmin({ adminId: admin._id }));
    }
  };

  const handleEditClick = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      password: "",
      adminRole: normalizeRole(admin.adminRole),
      extraPermissions: admin.adminPermissions || [],
    });
    setEditDialogOpen(true);
  };

  const handleViewClick = (admin) => {
    setSelectedAdmin(admin);
    setViewDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      adminRole: ADMIN_ROLES.REVIEWER,
      extraPermissions: [],
    });
    setSelectedAdmin(null);
    setShowPassword(false);
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
    setShowPassword(true);
  };

  const handleRefresh = () => {
    dispatch(GetAllAdmins());
  };

  const columns = [
    {
      field: "index",
      headerName: "#",
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "name",
      headerName: "Admin",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              bgcolor: `${ROLE_COLORS[params.row.adminRole]}20`,
              color: ROLE_COLORS[params.row.adminRole],
            }}
          >
            {(params.row.name || "A").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {params.row.name || "N/A"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "adminRole",
      headerName: "Role",
      width: 150,
      renderCell: (params) => (
        <Chip
          icon={<Security sx={{ fontSize: 14 }} />}
          label={ROLE_LABELS[params.value]}
          size="small"
          sx={{
            bgcolor: `${ROLE_COLORS[params.value]}15`,
            color: ROLE_COLORS[params.value],
            fontWeight: 500,
            "& .MuiChip-icon": { color: "inherit" },
          }}
        />
      ),
    },
    {
      field: "isAdminActive",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const isActive = params.value;
        const isPending = !params.row.is_register;

        if (isPending) {
          return (
            <Chip
              icon={<HourglassEmpty sx={{ fontSize: 14 }} />}
              label="Pending OTP"
              size="small"
              sx={{
                bgcolor: "#fef3c7",
                color: "#92400e",
                fontWeight: 500,
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          );
        }

        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? "Active" : "Inactive"}
            size="small"
            sx={{
              bgcolor: isActive ? "#dcfce7" : "#fee2e2",
              color: isActive ? "#166534" : "#991b1b",
              fontWeight: 500,
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        );
      },
    },
    {
      field: "lastLogin",
      headerName: "Last Login",
      width: 150,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleString() : "Never",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      renderCell: (params) => {
        const canEdit = isSuperAdmin || params.row.adminRole !== ADMIN_ROLES.SUPER_ADMIN;
        const canDelete = isSuperAdmin && params.row.adminRole !== ADMIN_ROLES.SUPER_ADMIN;
        const isPending = !params.row.is_register;

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => handleViewClick(params.row)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
            {isPending && isSuperAdmin && (
              <Tooltip title="Resend OTP">
                <IconButton
                  size="small"
                  onClick={() => {
                    setOtpEmail(params.row.email);
                    dispatch(ResendAdminOTP({ adminId: params.row._id })).then((result) => {
                      if (result.meta.requestStatus === "fulfilled") {
                        toast.success("OTP resent successfully!");
                      }
                    });
                  }}
                  sx={{ color: "#f59e0b" }}
                >
                  <Send fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && !isPending && (
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleEditClick(params.row)}>
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteAdmin(params.row)}
                  sx={{ color: "#ef4444" }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  // Check permission
  if (!userCan(currentUser, PERMISSIONS.VIEW_ADMINS)) {
    return (
      <>
        <AdminWrapper />
        <Box
          sx={{
            ml: { xs: 0, md: open ? "260px" : "65px" },
            transition: "margin 0.3s ease",
            minHeight: "100vh",
            bgcolor: "#f8fafc",
            p: { xs: 2, md: 3 },
            pt: { xs: 10, md: 11 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper sx={{ p: 4, textAlign: "center", maxWidth: 400 }}>
            <Security sx={{ fontSize: 60, color: "#ef4444", mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Access Denied
            </Typography>
            <Typography color="text.secondary">
              You don't have permission to access Admin Management.
              Only Super Admins can manage other administrators.
            </Typography>
          </Paper>
        </Box>
      </>
    );
  }

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Admin Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage administrator accounts and permissions
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              Create Admin
            </Button>
          </Box>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<MarkEmailRead />}>
          <Typography variant="body2">
            <strong>OTP Verification Required:</strong> When you create a new admin, an OTP will be sent to their email.
            The admin must verify the OTP to activate their account.
          </Typography>
        </Alert>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2, border: "1px solid #e5e7eb" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#6366f1" }}>
                  {admins?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Admins
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2, border: "1px solid #22c55e50" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#22c55e" }}>
                  {admins?.filter((a) => a.isAdminActive && a.is_register).length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Admins
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2, border: "1px solid #f59e0b50" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#f59e0b" }}>
                  {admins?.filter((a) => !a.is_register).length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending OTP
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ borderRadius: 2, border: "1px solid #ef444450" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#ef4444" }}>
                  {admins?.filter((a) => !a.isAdminActive && a.is_register).length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={filterRole}
                  label="Role"
                  onChange={(e) => setFilterRole(e.target.value)}
                >
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value={ADMIN_ROLES.SUPER_ADMIN}>Super Admin</MenuItem>
                  {ASSIGNABLE_ROLES.map((role) => (
                    <MenuItem key={role} value={role}>
                      {ROLE_LABELS[role]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                disabled={loading}
                sx={{ borderColor: "#e5e7eb" }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
          <DataGrid
            rows={filteredAdmins}
            columns={columns}
            getRowId={(row) => row._id}
            loading={loading}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={[10, 25]}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e5e7eb",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
              },
            }}
          />
        </Paper>

        {/* Create Admin Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SupervisorAccount sx={{ color: "#e94560" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Create New Admin
              </Typography>
            </Box>
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              An OTP will be sent to the admin's email for verification after creation.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password (min 8 characters)"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <Tooltip title="Generate Password">
                          <IconButton onClick={generatePassword} size="small">
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Admin Role</InputLabel>
                  <Select
                    value={formData.adminRole}
                    label="Admin Role"
                    onChange={(e) => setFormData({ ...formData, adminRole: e.target.value })}
                  >
                    {ASSIGNABLE_ROLES.map((role) => (
                      <MenuItem key={role} value={role}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Security sx={{ color: ROLE_COLORS[role], fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {ROLE_LABELS[role]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ROLE_DESCRIPTIONS[role]}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setCreateDialogOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleCreateAdmin}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Add />}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              {loading ? "Creating..." : "Create & Send OTP"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* OTP Verification Dialog */}
        <Dialog open={otpDialogOpen} onClose={() => {}} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <MarkEmailRead sx={{ fontSize: 40, color: "#22c55e" }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              OTP Sent Successfully!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mb: 3 }}
            >
              We've sent a 6-digit verification code to
              <br />
              <strong>{otpEmail}</strong>
            </Typography>

            {/* OTP Input Fields */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 3 }}>
              {otpValues.map((value, index) => (
                <TextField
                  key={index}
                  id={`otp-${index}`}
                  value={value}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: 24,
                      fontWeight: 700,
                      padding: "12px",
                    },
                  }}
                  sx={{
                    width: 56,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      "&.Mui-focused fieldset": {
                        borderColor: "#e94560",
                      },
                    },
                  }}
                />
              ))}
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                Didn't receive the code?{" "}
                {resendCooldown > 0 ? (
                  <span style={{ color: "#64748b" }}>Resend in {resendCooldown}s</span>
                ) : (
                  <Button
                    size="small"
                    onClick={handleResendOtp}
                    disabled={loading}
                    sx={{ color: "#e94560", textTransform: "none" }}
                  >
                    Resend OTP
                  </Button>
                )}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mt: 2 }}>
              The admin must verify this OTP to activate their account. OTP expires in 10 minutes.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={() => {
                setOtpDialogOpen(false);
                setOtpValues(["", "", "", "", "", ""]);
                dispatch(resetAdminOTPState());
                dispatch(GetAllAdmins());
              }}
              variant="outlined"
            >
              Close (Verify Later)
            </Button>
            <Button
              onClick={handleVerifyOtp}
              variant="contained"
              disabled={loading || otpValues.join("").length !== 6}
              sx={{ bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Edit Admin
            </Typography>
            <IconButton onClick={() => setEditDialogOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password (leave empty to keep current)"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Admin Role</InputLabel>
                  <Select
                    value={formData.adminRole}
                    label="Admin Role"
                    onChange={(e) => setFormData({ ...formData, adminRole: e.target.value })}
                    disabled={selectedAdmin?.adminRole === ADMIN_ROLES.SUPER_ADMIN && !isSuperAdmin}
                  >
                    {ASSIGNABLE_ROLES.map((role) => (
                      <MenuItem key={role} value={role}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Security sx={{ color: ROLE_COLORS[role], fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {ROLE_LABELS[role]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ROLE_DESCRIPTIONS[role]}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {selectedAdmin?.adminRole !== ADMIN_ROLES.SUPER_ADMIN && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Extra Permissions
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    On top of the role above — for splitting one job across specific people
                    instead of giving everyone full access.
                  </Typography>
                  <FormGroup>
                    {EXTRA_PERMISSION_OPTIONS.map((opt) => (
                      <FormControlLabel
                        key={opt.value}
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.extraPermissions.includes(opt.value)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.extraPermissions, opt.value]
                                : formData.extraPermissions.filter((p) => p !== opt.value);
                              setFormData({ ...formData, extraPermissions: next });
                            }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{opt.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opt.description}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditDialogOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAdmin}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}
            >
              {loading ? "Updating…" : "Update Admin"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Admin Dialog */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Admin Details
            </Typography>
            <IconButton onClick={() => setViewDialogOpen(false)}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedAdmin && (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: `${ROLE_COLORS[selectedAdmin.adminRole]}20`,
                      color: ROLE_COLORS[selectedAdmin.adminRole],
                      fontSize: 32,
                    }}
                  >
                    {(selectedAdmin.name || "A").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedAdmin.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedAdmin.email}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                      <Chip
                        label={ROLE_LABELS[selectedAdmin.adminRole]}
                        size="small"
                        sx={{
                          bgcolor: `${ROLE_COLORS[selectedAdmin.adminRole]}15`,
                          color: ROLE_COLORS[selectedAdmin.adminRole],
                        }}
                      />
                      {!selectedAdmin.is_register && (
                        <Chip
                          label="Pending OTP"
                          size="small"
                          sx={{ bgcolor: "#fef3c7", color: "#92400e" }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Permissions
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                  {getRolePermissions(selectedAdmin.adminRole)?.slice(0, 10).map((perm, idx) => (
                    <Chip
                      key={idx}
                      label={perm.replace(/_/g, " ").toLowerCase()}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  ))}
                  {getRolePermissions(selectedAdmin.adminRole)?.length > 10 && (
                    <Chip
                      label={`+${getRolePermissions(selectedAdmin.adminRole).length - 10} more`}
                      size="small"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Box>

                {selectedAdmin.adminPermissions?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Extra Permissions
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                      {selectedAdmin.adminPermissions.map((perm, idx) => (
                        <Chip
                          key={idx}
                          label={perm.replace(/_/g, " ").toLowerCase()}
                          size="small"
                          sx={{ fontSize: 11, bgcolor: "#eef2ff", color: "#4338ca" }}
                        />
                      ))}
                    </Box>
                  </>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedAdmin.isAdminActive ? "Active" : "Inactive"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(selectedAdmin.createdAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Last Login
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedAdmin.lastLogin
                        ? new Date(selectedAdmin.lastLogin).toLocaleString()
                        : "Never"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Email Verified
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedAdmin.is_register ? "Yes" : "No (Pending OTP)"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        theme="colored"
      />
    </>
  );
};

export default AdminManagement;
